import React, {useEffect, useRef, useState, useMemo} from 'react';
import {connect} from 'react-redux';
import {useDispatch} from 'react-redux';
import SelectedSong from '../../Components/SelectedSong/SelectedSong';
import MoreInfo from '../../Components/MoreInfo/MoreInfo';
import {
  submitPackageSelect,
  setIsChangePackageFlow,
  setIsAddServiceFlow,
  showToast,
  changePricePlan,
  saveToHistory,
  setSelectedPackage,
  setSelectedSubscriber,
  setCurrentScreen,
  ScreensEnum,
} from '../../FuntoneSlice';
import FuntoneButton from '../../Components/FuntoneButton/FuntoneButton';
import CheckBox from 'app/Components/Shared/CheckBox/Checkbox';
import PackageCard from '../../Components/PackageCard/PackageCard';
import {useScreenNavigation} from '../../Utils/ScreenNavigationUtils';
import {
  dwhFuntoneNewSubscriptionDownloadClick,
  dwhFuntoneNewSubscriptionSuccess,
  dwhFuntoneNewSubscriptionFailed,
  dwhFuntoneSongDownloadDisplayed,
  dwhFuntoneSongDownloadSuccess,
  dwhFuntoneSongDownloadFailed,
  dwhFuntoneChangePackage,
  dwhFuntoneChangePackageSuccess,
  dwhFuntoneChangePackageFailed,
} from '../../Reports/dwhReports';
import {getCommonReportParams} from '../../Reports/reportHelpers';

const PackageSelect = (props) => {
  const dispatch = useDispatch();
  const {navigateToTermsAndConditions} = useScreenNavigation();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');

  const {packages, downloadPageTitle, packageSelectTitle} = props.contentData || {};
  const {setSelectedPackage, submitPackageSelect, setIsChangePackageFlow, setSelectedSubscriber} = props;
  const {selectedSubscriber, selectedSong, selectedPackage, isChangePackageFlow, isAddServiceFlow, subscribersData} = props.funtoneSlice || {};

  // Clear selectedPackage when entering change package or add service flows
  useEffect(() => {
    if (isChangePackageFlow || isAddServiceFlow) {
      setSelectedPackage(null);
    }
  }, [isChangePackageFlow, isAddServiceFlow, setSelectedPackage]);

  // Report song download displayed (only when downloading a song with existing service)
  useEffect(() => {
    // This is a song download if: not changing package, not adding service, has selected song, and subscriber has service
    const isSongDownload = !isChangePackageFlow && !isAddServiceFlow;

    if (isSongDownload) {
      const reportParams = getCommonReportParams(selectedSubscriber, props.loginData);
      dwhFuntoneSongDownloadDisplayed({
        ...reportParams,
        ban: props.loginData?.userDetails?.ban || '',
        crmId: props.loginData?.userDetails?.crmId || '',
        songName: selectedSong?.description || selectedSong?.title || '',
      });
    }
  }, []);

  // Memoize expanded packages and count
  const expandedPackages = useMemo(() => {
    const packages = props.contentData?.packages?.expandedValue || [];
    // Filter out packages where hidePackage is true
    return packages.filter(pkg => pkg?.hidePackage?.value !== true);
  }, [props.contentData?.packages]);
  const packageCount = expandedPackages.length;

  // Filter out current package in change package flow
  const availablePackages = useMemo(() => {
    if (!isChangePackageFlow || !selectedSubscriber?.packageCode) {
      return expandedPackages;
    }
    // Exclude the current package when changing packages
    return expandedPackages.filter((pkg) => pkg.code?.value?.toUpperCase() !== selectedSubscriber.packageCode.toUpperCase());
  }, [isChangePackageFlow, expandedPackages, selectedSubscriber]);

  // Determine if user has service (but override in change package flow)
  const hasService = useMemo(() => {
    if (isChangePackageFlow) return false; // Treat as no service when changing package
    if (isAddServiceFlow) return false; // Treat as no service when adding service
    return selectedSubscriber?.hasService;
  }, [isChangePackageFlow, isAddServiceFlow, selectedSubscriber]);

  // Memoize title
  const title = useMemo(() => {
    if (isAddServiceFlow) {
      return selectedPackage ? 'מצוין, אישור שלך ונצרף אותך לשירות' : 'בחרו חבילה';
    }
    if (isChangePackageFlow && selectedPackage) {
      return 'מצוין, אישור שלך ונעדכן את החבילה';
    }
    if (isChangePackageFlow) {
      return  props.contentData?.changePricePlanTitle?.value || 'לאיזו חבילה היית רוצה לעבור?';
    }
    if (selectedPackage || hasService) {
      return downloadPageTitle?.value || 'בחר חבילה';
    }
    return packageSelectTitle?.value || 'בחר חבילה';
  }, [isAddServiceFlow, isChangePackageFlow, selectedPackage, hasService, downloadPageTitle, packageSelectTitle]);

  // Memoize display name for subtitle
  const subtitle = useMemo(() => {
    if (!selectedSubscriber) return '';
    const name = (selectedSubscriber.fullName || selectedSubscriber.firstName || '').trim();
    const cli = selectedSubscriber.cli || '';
    return name && cli ? `${name} | ${cli}` : name || cli;
  }, [selectedSubscriber]);

  // Memoize selected package for display
  const selectedPackageForDisplay = useMemo(() => {
    if (selectedPackage) return selectedPackage;
    // Don't auto-select current package in change package flow
    if (isChangePackageFlow) return null;
    if (expandedPackages.length && selectedSubscriber?.packageCode) {
      return expandedPackages.find((pkg) => pkg.code?.value?.toUpperCase() === selectedSubscriber.packageCode.toUpperCase());
    }
    return null;
  }, [selectedPackage, isChangePackageFlow, expandedPackages, selectedSubscriber]);

  // Auto-select package if only one (but not in change package flow).
  // hasAutoSelected prevents re-selection after the user presses back to deselect.
  const hasAutoSelected = useRef(false);
  useEffect(() => {
    if (!isChangePackageFlow && packageCount === 1 && expandedPackages[0] && !hasAutoSelected.current) {
      hasAutoSelected.current = true;
      setSelectedPackage(expandedPackages[0]);
    }
  }, [isChangePackageFlow, packageCount, expandedPackages, setSelectedPackage]);

  const handleOnCheck = (accepted) => setTermsAccepted(accepted);

  const handleSubmit = async () => {
    setTermsError('');
    if (termsAccepted) {
      // Report new subscription download click (only for new subscriptions)
      const isNewSubscription =
        !isChangePackageFlow && !isAddServiceFlow && selectedSong && selectedSubscriber && !selectedSubscriber.hasService;

      if (isNewSubscription) {
        const reportParams = getCommonReportParams(selectedSubscriber, props.loginData);
        const isExistingSubscriber = selectedSubscriber?.hasService ? 'מנוי קיים' : 'לא מנוי המתנה נעימה';
        dwhFuntoneNewSubscriptionDownloadClick({
          ...reportParams,
          ban: props.loginData?.userDetails?.ban || '',
          crmId: props.loginData?.userDetails?.crmId || '',
          isExistingSubscriber,
        });
      }

      let result;

      if (isChangePackageFlow) {
        // Change package flow - call changePricePlan
        const cli = selectedSubscriber?.cli;
        const newSocCode = selectedPackageForDisplay?.code?.value;

        if (!cli || !newSocCode) {
          dispatch(showToast('שגיאה: חסרים פרטים לשינוי החבילה'));
          return;
        }

        // Report change package initiated
        const reportParams = getCommonReportParams(selectedSubscriber, props.loginData);
        dwhFuntoneChangePackage({
          ...reportParams,
          ban: props.loginData?.userDetails?.ban || '',
          crmId: props.loginData?.userDetails?.crmId || '',
          packageType: selectedPackageForDisplay?.title?.value || selectedPackageForDisplay?.name || '',
        });

        result = await dispatch(
          changePricePlan({
            subscriber: cli,
            newSocCode: newSocCode,
          }),
        );

        // Report success/failure based on result
        if (result.type.endsWith('/fulfilled')) {
          dwhFuntoneChangePackageSuccess({
            ...reportParams,
            ban: props.loginData?.userDetails?.ban || '',
            crmId: props.loginData?.userDetails?.crmId || '',
            packageType: selectedPackageForDisplay?.title?.value || selectedPackageForDisplay?.name || '',
          });
          // Navigate to success page
          dispatch(setCurrentScreen(ScreensEnum.SUCCESS));
        }
      } else {
        // Regular purchase flow
        result = await dispatch(submitPackageSelect({
          selectedPackage: selectedPackageForDisplay,
          loginData: props.loginData
        }));

        // Report success/failure based on result
        if (result.type.endsWith('/fulfilled')) {
          // Success reporting moved to FuntoneSlice reducer
        } else if (result.type.endsWith('/rejected')) {
          if (isNewSubscription) {
            const reportParams = getCommonReportParams(selectedSubscriber, props.loginData);
            const isExistingSubscriber = selectedSubscriber?.hasService ? 'מנוי קיים' : 'לא מנוי המתנה נעימה';
            dwhFuntoneNewSubscriptionFailed({
              ...reportParams,
              ban: props.loginData?.userDetails?.ban || '',
              crmId: props.loginData?.userDetails?.crmId || '',
              isExistingSubscriber,
            });
          } else if (selectedSong && selectedSubscriber?.hasService) {
            const reportParams = getCommonReportParams(selectedSubscriber, props.loginData);
            dwhFuntoneSongDownloadFailed({
              ...reportParams,
              ban: props.loginData?.userDetails?.ban || '',
              crmId: props.loginData?.userDetails?.crmId || '',
              songName: selectedSong?.description || selectedSong?.title || '',
            });
          }
        }
      }
    } else {
      setTermsError('יש לאשר את תנאי השירות על מנת להמשיך');
    }
  };

  return (
    <div className={`package-select ${selectedPackage || hasService ? 'package-selected' : 'no-package-selected'}`}>
      <div className='top-content'>
        <span className='title'>{title}</span>
        <span className='subtitle'>{subtitle}</span>
      </div>
      <div className={`main-content ${(availablePackages.length >= 3 && !selectedPackage && !hasService) ? 'multiple-packages' : ''}`}>
        {!isChangePackageFlow && !isAddServiceFlow && selectedSong && (
          <SelectedSong
            title='השיר שבחרת'
            song={selectedSong}
            selectedPackage={selectedPackageForDisplay}
            className={selectedSubscriber && !selectedSubscriber.hasService && !selectedPackage ? '' : ''}
          />
        )}
        {isChangePackageFlow && selectedPackage && (
          <PackageCard
            packageItem={selectedPackage}
            isChangePackageFlow={isChangePackageFlow}
            selectedPackage={selectedPackage}
            onSelect={setSelectedPackage}
          />
        )}
        {!selectedPackage && !hasService && (
          <div className='packages-container'>
            {availablePackages.map((packageItem, index) => (
              <PackageCard packageItem={packageItem} key={index} onSelect={setSelectedPackage} isChangePackageFlow={isChangePackageFlow} />
            ))}
          </div>
        )}
        {(selectedPackage || hasService) && selectedPackageForDisplay?.moreInfo?.value?.length > 0 && (
          <MoreInfo selectedPackage={selectedPackageForDisplay} isChangePackageFlow={isChangePackageFlow} contentData={props.contentData} />
        )}
        {((selectedPackage || (selectedSong && selectedSubscriber.hasService && !isChangePackageFlow) || (selectedSong && selectedPackage))) && (
          <div className='terms-and-conditions'>
            <CheckBox id='terms-checkbox' onCheck={() => handleOnCheck(true)} onUncheck={() => handleOnCheck(false)} />
            <span className='terms-text'>
              <span> קראתי ואישרתי את </span>
              <a
                onClick={(e) => {
                  e.preventDefault();
                  dispatch(setSelectedPackage(selectedPackageForDisplay));
                  navigateToTermsAndConditions();
                }}>
                תנאי השירות
              </a>
              .
            </span>
          </div>
        )}
      </div>
      <div className='bottom-content'>
        <span className='error-text'>{termsError}</span>
        {(selectedPackage || hasService) && (
          <FuntoneButton
            variant='primary'
            text={isChangePackageFlow ? 'אישור ועדכון החבילה' : isAddServiceFlow ? 'אישור והצטרפות לשירות' : 'אישור והצטרפות'}
            onClick={handleSubmit}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  funtoneSlice: state.FuntoneSlice,
  loginData: state.authentication.loginData,
});

const mapDispatchToProps = {
  setSelectedPackage,
  submitPackageSelect,
  setIsChangePackageFlow,
  setSelectedSubscriber,
};

export default connect(mapStateToProps, mapDispatchToProps)(PackageSelect);
